/**
 * Component that shows when a feature is locked behind a plan upgrade.
 */

import type { ReactNode } from 'react';
import { Box, Typography, Button, Stack, Alert } from '@mui/material';
import { Lock as LockIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PlanFeature, PlanTier } from '@tilly/shared';
import { PLAN_METADATA } from '@tilly/shared';
import { colors } from '@/theme/tokens';

interface UpgradePromptProps {
  feature: PlanFeature;
  requiredTier?: PlanTier;
  children?: ReactNode;
}

export function UpgradePrompt({ requiredTier = 'solo', children }: UpgradePromptProps) {
  const metadata = PLAN_METADATA[requiredTier];
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(key as never, options as never) as unknown as string;

  return (
    <Box
      sx={{
        minHeight: children ? 'auto' : '300px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: colors.accentLight,
        borderRadius: 1,
      }}
    >
      <Stack spacing={2} alignItems="center" textAlign="center" maxWidth={400}>
        <Box sx={{ fontSize: 48, color: colors.accent }}>
          <LockIcon size={48} />
        </Box>
        <Typography variant="h6">{tr('upgradePrompt.title', { plan: metadata.name })}</Typography>
        <Typography variant="body2" color="text.secondary">
          {tr('upgradePrompt.descriptionPrefix')} <strong>{metadata.name}</strong>{' '}
          {tr('upgradePrompt.descriptionSuffix', { price: String(metadata.price) })}
        </Typography>
        <Alert severity="info" sx={{ width: '100%' }}>
          {tr('upgradePrompt.infoBanner')}
        </Alert>
        <Button
          variant="contained"
          color="inherit"
          startIcon={<LockIcon size={20} />}
          sx={{ mt: 1 }}
          onClick={() => navigate('/admin/settings?section=plan-billing')}
        >
          {tr('upgradePrompt.cta')}
        </Button>
      </Stack>
    </Box>
  );
}
